import React, { useState, useEffect } from 'react';
import { Typography, Link } from '@one-thd/sui-atomic-components';
import * as yup from 'yup';
import { getApiError } from '../../api/axios-util';
import { BULK_ADD_MAX_ERROR_FIELDS, BULK_ADD_MAX_USERS_LENGTH, } from '../../constants/general';
import { ERR_UNABLE_BULK_ADD } from '../../constants/response-error-messages';
import { getBulkAddSuccessMessage, getBulkAddSuccessMessageWithExpirationDate, } from '../../constants/response-success-messages';
import { TAlertStatus, TAlertType } from '../../models/common/alert-messages';
import Spinner from '../shared/spinner/spinner';
import { ImportResult } from './steps/import-result';
import { useAddManageUsers } from '../../hooks/use-add-manage-users';
import { Upload } from './steps/upload/upload-screen';
import { addManageUsersApi } from '../../api/b2b/add-manage-user';
import { setBulkAddEventAnalytics, setBulkAddPageAnalytics, setUserPostPageErrorAnalytics, } from '../../utils/add-manage-user-analytics';
import { getUserSchema } from './validation';
import { useModal } from '../../hooks/use-modal';
import { CancelImport, } from '../modal/bulk-add/cancel-import/cancel-import';
import { removeDuplicateUsers } from '../../utils/bulk-add';
import { checkIsInviteExpirationEnabled } from '../../utils/general';
// eslint-disable-next-line no-shadow
var BULK_STEP;
(function (BULK_STEP) {
    BULK_STEP["UPLOAD"] = "UPLOAD";
    BULK_STEP["IMPORT_RESULT"] = "IMPORT_RESULT";
})(BULK_STEP || (BULK_STEP = {}));
export var BulkAdd = function () {
    var _a = useAddManageUsers(), users = _a.users, isPartOfRunnerAllowance = _a.isPartOfRunnerAllowance, setFormMode = _a.setFormMode, setAlertMessage = _a.setAlertMessage;
    var _b = useModal(), openModal = _b.openModal, hideModal = _b.hideModal;
    var _c = useState(false), isLoading = _c[0], setIsLoading = _c[1];
    var _d = useState(BULK_STEP.UPLOAD), step = _d[0], setStep = _d[1];
    var _e = useState({
        initialValidUsers: [],
        invalidUsers: [],
    }), filteredUsersList = _e[0], setFilteredUsersList = _e[1];
    useEffect(function () {
        setBulkAddPageAnalytics();
    }, []);
    var maxWidth = step === BULK_STEP.UPLOAD
        ? 'sui-max-w-[672px]'
        : 'sui-max-w-[1000px] lg:sui-pr-[92px]';
    var isUploadStep = step === BULK_STEP.UPLOAD;
    var isMaxErrors = filteredUsersList.invalidUsers.length > BULK_ADD_MAX_ERROR_FIELDS;
    var isMaxUsers = filteredUsersList.initialValidUsers.length +
        filteredUsersList.invalidUsers.length >
        BULK_ADD_MAX_USERS_LENGTH;
    var shouldShowImportForm = !isUploadStep && !isMaxUsers && !isMaxErrors;
    var isExpirationEnabled = checkIsInviteExpirationEnabled();
    var setUploadStep = function () { return setStep(BULK_STEP.UPLOAD); };
    var validateUploadScreen = function (newUserValues) {
        var errors = newUserValues.map(function (item) {
            try {
                var validationUserSchema = getUserSchema(isPartOfRunnerAllowance);
                validationUserSchema.validateSync(item);
            }
            catch (error) {
                if (error instanceof yup.ValidationError)
                    return error.errors;
            }
            return null;
        });
        var isValid = errors.every(function (err) { return !err; }) &&
            newUserValues.length <= BULK_ADD_MAX_USERS_LENGTH;
        if (!isValid) {
            var validatedFields = newUserValues.reduce(function (acc, user, index) {
                if (!errors[index]) {
                    acc.initialValidUsers.push(user);
                }
                else {
                    acc.invalidUsers.push(user);
                }
                return acc;
            }, {
                initialValidUsers: [],
                invalidUsers: [],
            });
            setFilteredUsersList(validatedFields);
            setBulkAddEventAnalytics("validation: mixed: success>".concat(validatedFields.initialValidUsers.length, ": fail>").concat(validatedFields.invalidUsers.length));
        }
        else {
            setBulkAddEventAnalytics("import: success>".concat(newUserValues.length));
        }
        return isValid;
    };
    var onSubmit = function (newUserValues) {
        var transformedUserValues = removeDuplicateUsers(users, newUserValues);
        var isNotValidUpload = isUploadStep && !validateUploadScreen(transformedUserValues);
        if (isNotValidUpload) {
            setStep(BULK_STEP.IMPORT_RESULT);
            return;
        }
        setIsLoading(true);
        addManageUsersApi
            .bulkAdd(transformedUserValues)
            .then(function (_a) {
            var data = _a.data;
            setBulkAddEventAnalytics("add success: ".concat(transformedUserValues.length));
            setAlertMessage({
                text: isExpirationEnabled &&
                    'userInviteExpirationDate' in data &&
                    (data === null || data === void 0 ? void 0 : data.userInviteExpirationDate)
                    ? getBulkAddSuccessMessageWithExpirationDate(transformedUserValues.length, data.userInviteExpirationDate).message
                    : getBulkAddSuccessMessage(transformedUserValues.length).message,
                alertStatus: TAlertStatus.INFO,
                type: TAlertType.GENERAL,
            });
        })
            .catch(function (error) {
            var errorCode = getApiError(error, 'users').errorCode || 'n/a';
            setAlertMessage({
                text: ERR_UNABLE_BULK_ADD.message,
                alertStatus: TAlertStatus.ERROR,
                type: TAlertType.GENERAL,
            });
            setUserPostPageErrorAnalytics('bulk add', 'inline', errorCode, ERR_UNABLE_BULK_ADD.message);
        })
            .finally(function () {
            setIsLoading(false);
            setFormMode(null);
        });
    };
    var handleCancelImportNoValidUsers = function () {
        setFormMode(null);
        hideModal();
    };
    var handleCancelImport = function () {
        if (shouldShowImportForm) {
            openModal(CancelImport, {
                onConfirmCancel: function () {
                    setFormMode(null);
                    hideModal();
                },
                hideModal: hideModal,
            });
        }
        else {
            setFormMode(null);
        }
    };
    var renderSteps = function () {
        switch (step) {
            case BULK_STEP.UPLOAD:
                return React.createElement(Upload, { onSubmit: onSubmit });
            case BULK_STEP.IMPORT_RESULT:
                return (React.createElement(ImportResult, { shouldShowImportForm: shouldShowImportForm, isMaxErrors: isMaxErrors, isMaxUsers: isMaxUsers, setUploadStep: setUploadStep, handleCancelImport: handleCancelImport, handleCancelImportNoValidUsers: handleCancelImportNoValidUsers, filteredUsersList: filteredUsersList, onSubmit: onSubmit }));
            default:
                return null;
        }
    };
    return (React.createElement("div", { className: maxWidth },
        React.createElement(Spinner, { active: isLoading }),
        React.createElement("div", { className: "sui-flex sui-justify-between sui-mb-8" },
            React.createElement(Typography, { variant: "h1", weight: "regular" }, "BULK ADD USERS"),
            React.createElement(Link, { component: "button", onClick: handleCancelImport, "data-testid": "cancelImportTopBtn" }, shouldShowImportForm ? 'Cancel Import' : 'Cancel')),
        renderSteps()));
};
